import * as React from "react";
import * as TablerIcons from "@tabler/icons-react";
import { IconCheck, IconChevronRight } from "@tabler/icons-react";
import styles from "./NavigationButton.module.css";

export interface NavigationButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  text: React.ReactNode;
  leftIcon?: string | React.ReactNode;
  rightIcon?: string | React.ReactNode;
  info?: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
  variant?: "default" | "delete" | "docs" | "border" | "insights";
  greyVariant?: boolean;
  completed?: boolean;
}

const renderIcon = (icon?: string | React.ReactNode, strokeColor?: string) => {
  if (!icon) return null;

  if (typeof icon === "string") {
    const iconName =
      "Icon" +
      icon
        .split("-")
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join("");

    const IconComponent = (TablerIcons as Record<string, unknown>)[iconName];
    if (IconComponent) return <IconComponent stroke={strokeColor} />;
    return null;
  }

  if (React.isValidElement(icon) && strokeColor) {
    return React.cloneElement(icon as React.ReactElement, {
      stroke: strokeColor,
    });
  }

  return icon;
};

const NavigationButton = React.forwardRef<
  HTMLButtonElement,
  NavigationButtonProps
>(
  (
    {
      text,
      leftIcon,
      rightIcon,
      className,
      description,
      variant = "default",
      greyVariant = false,
      ...props
    },
    ref
  ) => {
    /** BUTTON CLASS */
    const insightsButtonClass =
      variant === "insights"
        ? props.completed
          ? `${styles.navigationButton__insights} completed`
          : styles.navigationButton__insights
        : null;

    const buttonClass = [
      styles.navigationButton,
      variant === "delete" ? styles.navigationButton__delete : null,
      variant === "docs" ? styles.navigationButton__docs : null,
      variant === "border" ? styles.navigationButton__border : null,
      insightsButtonClass,
      className,
    ]
      .filter(Boolean)
      .join(" ");

    /** RIGHT ICON ALWAYS DEFAULT */
    const rightIconClass = styles.navigationButton__icon;

    /** Helper functions to reduce cognitive complexity */
    const getLeftIconClass = () => {
      const classes = [
        styles.navigationButton__icon,
        variant === "delete" ? styles.navigationButton__icon__delete : null,
        variant === "docs" ? styles.navigationButtonIconPillDocs : null,
        variant === "insights" ? styles.navigationButton__icon__insights : null,
        greyVariant ? styles.navigationButton__iconGrey : null,
      ];
      return classes.filter(Boolean).join(" ");
    };

    const getTextClass = () => {
      const classes = [
        styles.navigationButton__text,
        variant === "delete" ? styles.navigationButton__text__delete : null,
      ];
      return classes.filter(Boolean).join(" ");
    };

    /** Stroke color logic for left icon */
    const leftStrokeColor = greyVariant
      ? "#474747"
      : variant === "docs"
      ? "#45168C"
      : undefined;

    /** Color inheritance for docs pill */
    const leftIconStyle = greyVariant
      ? { color: "#474747" }
      : variant === "docs"
      ? { color: "#45168C" }
      : undefined;

    /**
     * Ensure docs left icon has explicit size so it doesn't render tiny
     * even if CSS width/height are overridden by inline SVG attributes.
     */
    const renderLeftIcon = () => {
      if (!leftIcon) return null;

      // For docs variant and React elements, force explicit Tabler size
      if (variant === "docs" && React.isValidElement(leftIcon)) {
        return React.cloneElement(leftIcon as React.ReactElement, {
          ...leftIcon.props,
          size: 16,
          stroke: leftStrokeColor,
        });
      }

      // Fallback to generic icon renderer
      return renderIcon(leftIcon, leftStrokeColor);
    };

    /** Get right icon content based on variant and completion state */
    const getRightIconContent = () => {
      if (variant === "insights") {
        return props.completed ? (
          <div
            style={{
              display: "flex",
              width: "20px",
              height: "20px",
              justifyContent: "center",
              alignItems: "center",
              flexShrink: 0,
              borderRadius: "50%",
              background: "#217B57",
              color: "#FFFFFF",
            }}
          >
            <IconCheck size={14} strokeWidth={2.5} color="#FFFFFF" />
          </div>
        ) : (
          <IconChevronRight size={24} strokeWidth={2} color="#45168C" />
        );
      }
      return renderIcon(rightIcon);
    };

    return (
      <button
        ref={ref}
        className={buttonClass}
        style={description ? { height: 75 } : undefined}
        {...props}
      >
        {/* LEFT ICON */}
        {leftIcon && (
          <span className={getLeftIconClass()} style={leftIconStyle}>
            {renderLeftIcon()}
          </span>
        )}

        {/* TEXT BLOCK */}
        <span className={styles.navigationButton__textBlock}>
          <span
            className={getTextClass()}
            style={greyVariant ? { color: "#474747" } : undefined}
          >
            {text}
          </span>

          {description && (
            <span className={styles.navigationButton__description}>
              {description}
            </span>
          )}
        </span>

        {/* INFO TEXT */}
        {props.info && (
          <span
            style={{
              marginRight: 8,
              fontWeight: 600,
              fontSize: 12,
              color: "#45168C",
              display: "flex",
              alignItems: "center",
            }}
          >
            {props.info}
          </span>
        )}

        {/* RIGHT ICON */}
        {(rightIcon || variant === "insights") && (
          <span className={rightIconClass}>{getRightIconContent()}</span>
        )}
      </button>
    );
  }
);

NavigationButton.displayName = "NavigationButton";

export default NavigationButton;
export { NavigationButton };
